<template>
  <section class="family-category-card">
    <header class="family-category-card__header">
      <span class="family-category-card__icon">
        <i class="fas fa-layer-group" />
      </span>
      <div class="family-category-card__heading">
        <h3 class="family-category-card__title">{{ label }}</h3>
        <p class="family-category-card__count">
          {{
            $tc('familyCategories.familiesCount', families.length, {
              count: families.length,
            })
          }}
        </p>
      </div>
      <el-dropdown
        v-if="canManage"
        trigger="click"
        placement="bottom-end"
        @command="(command) => $emit(command)"
      >
        <button
          type="button"
          class="family-category-card__menu"
          :aria-label="$t('familyCategories.actions')"
        >
          <i class="fas fa-ellipsis-h" />
        </button>
        <el-dropdown-menu slot="dropdown">
          <el-dropdown-item
            command="rename"
            icon="el-icon-edit"
          >
            {{ $t('familyCategories.rename') }}
          </el-dropdown-item>
          <el-dropdown-item
            command="delete"
            icon="el-icon-delete"
            class="family-category-card__delete"
          >
            {{ $t('familyCategories.delete') }}
          </el-dropdown-item>
        </el-dropdown-menu>
      </el-dropdown>
    </header>

    <transition-group
      v-if="families.length"
      name="family-pill"
      tag="ul"
      class="family-category-card__families"
    >
      <li
        v-for="familyType in families"
        :key="familyType.familyCode"
        class="family-pill"
      >
        <span class="family-pill__code">{{ familyType.familyCode }}</span>
        <span
          v-if="familyType.family"
          class="family-pill__name"
          :title="familyType.family"
          >{{ familyType.family }}</span
        >
        <el-popconfirm
          v-if="canManage"
          :title="
            $t('familyCategories.removeConfirm', {
              family: familyType.familyCode,
              category: label,
            })
          "
          :confirm-button-text="$t('familyCategories.remove')"
          :cancel-button-text="$t('familyCategories.cancel')"
          icon="el-icon-warning-outline"
          icon-color="#f59e0b"
          @confirm="$emit('remove', familyType)"
        >
          <button
            slot="reference"
            type="button"
            class="family-pill__remove"
            :aria-label="$t('familyCategories.remove')"
          >
            <i class="fas fa-times" />
          </button>
        </el-popconfirm>
      </li>
    </transition-group>

    <p
      v-else
      class="family-category-card__empty"
    >
      <i class="fal fa-layer-group" />
      {{ $t('familyCategories.empty') }}
    </p>

    <footer
      v-if="canManage"
      class="family-category-card__footer"
    >
      <div
        v-if="adding"
        class="family-category-card__search"
      >
        <el-autocomplete
          ref="search"
          v-model="query"
          :fetch-suggestions="suggest"
          :trigger-on-focus="false"
          :placeholder="$t('familyCategories.searchPlaceholder')"
          prefix-icon="el-icon-search"
          value-key="familyCode"
          popper-class="family-category-search"
          @select="select"
          @keyup.esc.native="stopAdding"
        >
          <template slot-scope="{ item }">
            <div class="family-suggestion">
              <span class="family-suggestion__code">{{ item.familyCode }}</span>
              <span class="family-suggestion__name">{{ item.family }}</span>
              <span
                v-if="item.currentCategory"
                class="family-suggestion__hint"
              >
                {{
                  $t('familyCategories.alreadyIn', {
                    category: item.currentCategory,
                  })
                }}
              </span>
            </div>
          </template>
        </el-autocomplete>
        <el-button
          type="text"
          class="family-category-card__cancel"
          @click="stopAdding"
        >
          {{ $t('familyCategories.cancel') }}
        </el-button>
      </div>
      <el-button
        v-else
        type="text"
        class="family-category-card__add"
        @click="startAdding"
      >
        <i class="fas fa-plus" />
        {{ $t('familyCategories.add') }}
      </el-button>
    </footer>
  </section>
</template>

<script>
  import { analysesApi } from '@/api';

  export default {
    name: 'FamilyCategoryCard',
    props: {
      label: { type: String, required: true },
      families: { type: Array, default: () => [] },
      // familyCode → label of the category the family is in, across all categories
      categoryLabelByFamily: { type: Object, default: () => ({}) },
      canManage: { type: Boolean, default: false },
    },
    data() {
      return {
        adding: false,
        query: '',
      };
    },
    methods: {
      startAdding() {
        this.adding = true;
        this.$nextTick(() => this.$refs.search.focus());
      },
      stopAdding() {
        this.adding = false;
        this.query = '';
      },
      async suggest(text, callback) {
        if (!text.trim()) {
          callback([]);
          return;
        }
        try {
          const { data } = await analysesApi.searchFamilies(text.trim());
          const inThisCategory = new Set(
            this.families.map((familyType) => familyType.familyCode)
          );
          const seen = new Set();
          callback(
            data
              .filter(({ familyCode }) => {
                if (inThisCategory.has(familyCode) || seen.has(familyCode)) {
                  return false;
                }
                seen.add(familyCode);
                return true;
              })
              .map(({ familyCode, family }) => ({
                familyCode,
                family,
                currentCategory: this.categoryLabelByFamily[familyCode],
              }))
          );
        } catch (error) {
          callback([]);
        }
      },
      select({ familyCode, family }) {
        this.$emit('add', { familyCode, family });
        this.stopAdding();
      },
    },
  };
</script>

<style lang="scss" scoped>
  $card-background: #2e3454;
  $ease: cubic-bezier(0.4, 0, 0.2, 1);

  .family-category-card {
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 24px;
    border: 1px solid rgba(#fff, 0.06);
    border-radius: 16px;
    background: $card-background;
    box-shadow: 0 10px 25px -5px rgba(#000, 0.3),
      0 4px 12px -2px rgba(#000, 0.2);
    transition: border-color 0.2s $ease, box-shadow 0.2s $ease;

    &:hover {
      border-color: rgba(#fff, 0.1);
      box-shadow: 0 20px 30px -8px rgba(#000, 0.4);
    }

    &__header {
      display: flex;
      align-items: center;
      gap: 14px;
    }

    &__icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex: none;
      width: 40px;
      height: 40px;
      border-radius: 12px;
      background: rgba($--color-primary, 0.14);
      color: $--color-primary;
      font-size: 16px;
    }

    &__heading {
      flex: 1;
      min-width: 0;
    }

    &__menu {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 32px;
      height: 32px;
      padding: 0;
      border: 0;
      border-radius: 10px;
      background: transparent;
      color: $--color-text-regular;
      cursor: pointer;
      transition: background 0.15s $ease, color 0.15s $ease;

      &:hover,
      &:focus-visible {
        background: rgba(#fff, 0.08);
        color: $--color-white;
        outline: none;
      }
    }

    &__title {
      margin: 0;
      color: $--color-white;
      font-size: 18px;
      font-weight: 700;
      letter-spacing: -0.01em;
    }

    &__count {
      margin: 2px 0 0;
      color: $--color-text-regular;
      font-size: 13px;
    }

    &__families {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin: 0;
      padding: 0;
      list-style: none;
    }

    &__empty {
      display: flex;
      align-items: center;
      gap: 10px;
      margin: 0;
      padding: 18px 16px;
      border: 1px dashed rgba(#fff, 0.12);
      border-radius: 12px;
      color: $--color-text-regular;
      font-size: 13px;

      i {
        font-size: 18px;
        opacity: 0.7;
      }
    }

    &__footer {
      padding-top: 16px;
      border-top: 1px solid rgba(#fff, 0.06);
    }

    &__add {
      padding: 0;
      color: $--color-primary;
      font-size: 14px;
      font-weight: 600;

      i {
        margin-right: 6px;
        font-size: 12px;
      }

      &:hover,
      &:focus {
        color: mix($--color-white, $--color-primary, 20%);
      }
    }

    &__search {
      display: flex;
      align-items: center;
      gap: 12px;

      .el-autocomplete {
        flex: 1;
      }
    }

    &__cancel {
      padding: 0;
      color: $--color-text-regular;
      font-weight: 600;

      &:hover,
      &:focus {
        color: $--color-white;
      }
    }
  }

  .family-pill {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    max-width: 100%;
    padding: 6px 6px 6px 12px;
    border: 1px solid rgba(#fff, 0.08);
    border-radius: 999px;
    background: rgba(#fff, 0.05);
    font-size: 13px;
    line-height: 20px;
    transition: background 0.2s $ease, border-color 0.2s $ease;

    &:hover,
    &:focus-within {
      border-color: rgba(#fff, 0.16);
      background: rgba(#fff, 0.09);

      .family-pill__remove {
        opacity: 1;
      }
    }

    &__code {
      color: $--color-white;
      font-weight: 700;
      letter-spacing: 0.02em;
    }

    &__name {
      overflow: hidden;
      max-width: 220px;
      color: $--color-text-regular;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    &__remove {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 20px;
      height: 20px;
      padding: 0;
      border: 0;
      border-radius: 50%;
      background: transparent;
      color: $--color-text-regular;
      font-size: 10px;
      cursor: pointer;
      opacity: 0;
      transition: opacity 0.15s $ease, background 0.15s $ease, color 0.15s $ease;

      &:hover,
      &:focus-visible {
        background: rgba(#fff, 0.14);
        color: $--color-white;
        opacity: 1;
        outline: none;
      }

      // No hover on touch screens: keep the action visible
      @media (hover: none) {
        opacity: 1;
      }
    }
  }

  // Families appear and leave with a soft scale, the others glide into place
  .family-pill-enter-active,
  .family-pill-leave-active {
    transition: opacity 0.25s $ease, transform 0.25s $ease;
  }

  .family-pill-enter,
  .family-pill-leave-to {
    opacity: 0;
    transform: scale(0.9);
  }

  .family-pill-move {
    transition: transform 0.25s $ease;
  }
</style>

<style lang="scss">
  // The dropdown menu is appended to <body>, outside the scoped styles
  .el-dropdown-menu__item.family-category-card__delete {
    color: #f87171;

    &:hover,
    &:focus {
      background: rgba(#ef4444, 0.12);
      color: #fca5a5;
    }
  }

  // The suggestion list is appended to <body>, outside the scoped styles
  .family-category-search {
    .family-suggestion {
      display: flex;
      align-items: center;
      gap: 10px;
      min-width: 0;

      &__code {
        flex: none;
        font-weight: 700;
      }

      &__name {
        overflow: hidden;
        flex: 1;
        opacity: 0.75;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      &__hint {
        flex: none;
        padding: 0 8px;
        border-radius: 999px;
        background: rgba($--color-primary, 0.14);
        color: $--color-primary;
        font-size: 12px;
        line-height: 20px;
      }
    }
  }
</style>
